import {
  CreditApplicationColumnType,
  CreditApplicationFields,
  CreditApplicationTemplate,
  InternalFormSubmission,
} from '@dill/dill-shared';

// "External Form" for forms sent outside the supplier (formType EXTERNAL), else "Internal Form".
export const getFormTypeLabel = (submission: Pick<InternalFormSubmission, 'formType'>) =>
  submission.formType === 'EXTERNAL' ? 'External Form' : 'Internal Form';

export type InternalFormSection = {
  key: string;
  stepName: string;
  title: string;
  columnType: CreditApplicationColumnType;
  fields: CreditApplicationFields[];
  // Sections sharing a borderGroup are drawn inside one bordered box (see getBorderGroups).
  borderGroup: string | null;
};

// Same rule as DynamicStepForm.tsx: within a step, an untitled section whose field has a
// `borderId` starts a bordered box that runs through the section holding the field with that id.
// Returns each section's box key, by the section's index in the step (null when not boxed).
const getBorderGroups = (
  stepId: string,
  sections: { groupName?: string; fields: CreditApplicationFields[] }[]
): (string | null)[] => {
  const sectionIndexByFieldId: Record<string, number> = {};
  sections.forEach((section, index) =>
    section.fields.forEach((field) => {
      if (field.id) sectionIndexByFieldId[field.id] = index;
    })
  );
  const groups: (string | null)[] = sections.map(() => null);
  let skipUntil = -1;
  sections.forEach((section, index) => {
    if (index <= skipUntil || section.groupName) return;
    const borderId = section.fields.find((field) => field.borderId)?.borderId;
    const endIndex = borderId ? sectionIndexByFieldId[borderId] : undefined;
    if (endIndex === undefined || endIndex < index) return;
    for (let i = index; i <= endIndex; i++) groups[i] = `${stepId}-border-${index}`;
    skipUntil = endIndex;
  });
  return groups;
};

// An internal form renders every step on one page, in order. A GROUPED section only uses its
// first group (no "add another" rows) — mirrors flattenInternalFormTemplateFields on the backend.
export const getInternalFormSections = (
  template: CreditApplicationTemplate
): InternalFormSection[] =>
  [...template.steps]
    .sort((a, b) => a.position - b.position)
    .flatMap((step) => {
      const sections = [...step.fields]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((section, index) => ({
          key: `${step.id}-${section.groupName ?? ''}-${section.position ?? index}`,
          stepName: step.name,
          title: section.groupName ?? '',
          groupName: section.groupName,
          columnType: section.columnType,
          fields:
            section.stepType === 'GROUPED'
              ? (section.groupedFields ?? [])
                  .filter((group) => (group.groupedPosition ?? 0) === 0)
                  .flatMap((group) => group.fields)
              : section.unGroupedFields ?? [],
        }));
      const borderGroups = getBorderGroups(step.id, sections);
      return sections.map(({ groupName, ...section }, index) => ({
        ...section,
        borderGroup: borderGroups[index],
      }));
    })
    .filter((section) => section.fields.length > 0);

// Same rule CreditApplicationRequestForm applies on submit: a field with conditionalVisibility only
// shows while its source field's value is one of sourceOptionValues. Chained too — a field whose
// source is itself hidden stays hidden, so a stale answer left behind in a collapsed section (e.g.
// "Tax exempt? Yes" under an address change that's since been switched to "No") can't keep its
// dependants showing. `fieldsById` is every field on the form.
export const isInternalFormFieldVisible = (
  field: CreditApplicationFields,
  values: Record<string, any>,
  fieldsById: Record<string, CreditApplicationFields> = {},
  seen: Set<string> = new Set()
): boolean => {
  if (!field.conditionalVisibility?.sourceFieldId) return true;
  const { sourceFieldId, sourceOptionValues } = field.conditionalVisibility;
  if (!(sourceOptionValues ?? []).includes(String(values[sourceFieldId] ?? ''))) return false;
  const source = fieldsById[sourceFieldId];
  if (!source || seen.has(field.id)) return true;
  return isInternalFormFieldVisible(source, values, fieldsById, new Set(seen).add(field.id));
};

// Consecutive sections with the same borderGroup, in order; ungrouped sections are runs of one.
export const groupSectionsByBorder = (sections: InternalFormSection[]) =>
  sections.reduce<{ borderGroup: string | null; sections: InternalFormSection[] }[]>(
    (runs, section) => {
      const last = runs[runs.length - 1];
      if (last && section.borderGroup && last.borderGroup === section.borderGroup) {
        last.sections.push(section);
      } else {
        runs.push({ borderGroup: section.borderGroup, sections: [section] });
      }
      return runs;
    },
    []
  );

const COLUMN_COUNT: Record<CreditApplicationColumnType, number> = {
  ONE_COLUMN: 1,
  TWO_COLUMNS: 2,
  THREE_COLUMNS: 3,
};

// Row sizes for InternalSignatureDocumentFillableFields' `groupSizes`, e.g. 5 fields in two
// columns -> [2, 2, 1].
export const getSectionRowSizes = (fieldCount: number, columnType: CreditApplicationColumnType) => {
  const columns = COLUMN_COUNT[columnType] ?? 1;
  const sizes: number[] = [];
  for (let remaining = fieldCount; remaining > 0; remaining -= columns) {
    sizes.push(Math.min(columns, remaining));
  }
  return sizes;
};
